import React from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Breadcrumbs,
    Link,
    Avatar
} from "@mui/material";
import {
    History as HistoryIcon,
    Refresh as RefreshIcon,
    NavigateNext as NavigateNextIcon,
    Home as HomeIcon,
    Campaign as CampaignIcon,
    GroupWork as GroupWorkIcon,
    FileDownload as FileDownloadIcon,
    Settings as SettingsIcon
} from "@mui/icons-material";

export default function CampaignHeader({ onRefresh, onOpenExportHistory, onBulkJoin, onExportCandidates, onManageTriggers, isLoading, campaignCount = 0, exportStatus = 'idle' }) {
    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 3
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Avatar
                        sx={{
                            width: 64,
                            height: 64,
                            bgcolor: '#ff9800',
                            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                        }}
                    >
                        <CampaignIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    
                    <Box>
                        <Typography 
                            variant="h3" 
                            component="h1"
                            sx={{ 
                                color: '#1a1a1a',
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', md: '2.5rem' },
                                lineHeight: 1.2,
                                mb: 0.5
                            }}
                        >
                            Campanhas de Promoção
                        </Typography>
                        <Typography 
                            variant="h6" 
                            color="text.secondary"
                            sx={{ 
                                fontSize: '1rem',
                                fontWeight: 400,
                                mb: 1
                            }}
                        >
                            Gerencie e monitore suas campanhas promocionais do Mercado Livre
                        </Typography>
                        <Box sx={{
                            bgcolor: '#fff3e0',
                            color: '#ff9800',
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            display: 'inline-block'
                        }}>
                            {campaignCount} campanhas encontradas
                        </Box>
                    </Box>
                </Box>
                
                <Box sx={{ 
                    display: 'flex', 
                    gap: 2,
                    alignItems: 'center',
                    flexShrink: 0
                }}>
                    <Tooltip title="Atualizar campanhas" arrow>
                        <IconButton 
                            onClick={onRefresh} 
                            disabled={isLoading}
                            sx={{
                                bgcolor: 'white',
                                border: '2px solid #f0f0f0',
                                width: 48,
                                height: 48,
                                '&:hover': { 
                                    bgcolor: '#fff3e0',
                                    borderColor: '#ff9800',
                                    transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <RefreshIcon sx={{ 
                                color: '#ff9800',
                                animation: isLoading ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }} />
                        </IconButton>
                    </Tooltip>
                    
                    <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={onManageTriggers}
                        disabled={true}
                        size="large"
                        sx={{
                            borderColor: '#e0e0e0',
                            color: '#bdbdbd',
                            '&:disabled': {
                                borderColor: '#e0e0e0',
                                color: '#bdbdbd'
                            },
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1.5,
                            px: 3,
                            borderRadius: 2,
                            fontSize: '1rem',
                            borderWidth: 2
                        }}
                    >
                        Gatilhos
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={onExportCandidates}
                        disabled={exportStatus === 'processing'}
                        size="large"
                        sx={{
                            borderColor: '#4caf50',
                            color: '#4caf50',
                            '&:hover': { 
                                borderColor: '#388e3c',
                                bgcolor: 'rgba(76, 175, 80, 0.1)',
                                transform: 'translateY(-2px)'
                            },
                            '&:disabled': {
                                borderColor: '#e0e0e0',
                                color: '#bdbdbd'
                            },
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1.5,
                            px: 3,
                            borderRadius: 2,
                            fontSize: '1rem',
                            transition: 'all 0.2s ease',
                            borderWidth: 2,
                            '&:hover': {
                                borderWidth: 2
                            }
                        }}
                    >
                        {exportStatus === 'processing' ? 'Processando...' : 'Exportar Campanhas'}
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<GroupWorkIcon />}
                        onClick={onBulkJoin}
                        size="large"
                        sx={{
                            borderColor: '#ff9800',
                            color: '#ff9800',
                            '&:hover': { 
                                borderColor: '#f57c00',
                                bgcolor: 'rgba(255, 152, 0, 0.1)',
                                transform: 'translateY(-2px)'
                            },
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1.5,
                            px: 3,
                            borderRadius: 2,
                            fontSize: '1rem',
                            transition: 'all 0.2s ease',
                            borderWidth: 2,
                            '&:hover': {
                                borderWidth: 2
                            }
                        }}
                    >
                        Aderir Em Massa
                    </Button>
                    
                    <Button
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={onOpenExportHistory}
                        size="large"
                        sx={{
                            borderColor: '#3483fa',
                            color: '#3483fa',
                            bgcolor: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1.5,
                            px: 3,
                            borderRadius: 2,
                            fontSize: '1rem',
                            borderWidth: 2,
                            transition: 'all 0.2s ease',
                            '& .MuiButton-startIcon svg': {
                                transition: 'transform 0.3s ease'
                            },
                            '&:hover': {
                                borderWidth: 2,
                                borderColor: '#2968c8',
                                color: '#2968c8',
                                bgcolor: 'rgba(52, 131, 250, 0.08)'
                            },
                            '&:hover .MuiButton-startIcon svg': {
                                transform: 'rotate(-30deg)'
                            }
                        }}
                    >
                        Histórico
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
